/**
 * Canonical ordering of the dense feature vector. This tuple is the single
 * source of truth for how a {@link FeatureVector} maps to/from a flat
 * `number[]`; every consumer (classifier, training corpus, diagnostics) MUST
 * use this ordering so vectors stay comparable across space and time.
 */
export const FEATURE_NAMES = [
    "centroid",
    "noise",
    "voicing",
    "energy",
    "transient",
    "flatness",
    "rolloff",
    "tilt",
    "spread",
    "crest",
    "flux",
    "zcr",
    "hnr",
    "pitchHz",
    "pitchConf",
    "pitchClass",
];
export const FEATURE_DIM = FEATURE_NAMES.length;
/** Feature names that describe pitch (masked for pitch-invariant labels). */
export const PITCH_FEATURE_NAMES = [
    "pitchHz",
    "pitchConf",
    "pitchClass",
];
export const PITCH_FEATURE_INDICES = FEATURE_NAMES.reduce((acc, name, i) => {
    if (PITCH_FEATURE_NAMES.includes(name)) {
        acc.push(i);
    }
    return acc;
}, []);
// O(1) membership test used by the masking hot path.
const PITCH_INDEX_SET = new Set(PITCH_FEATURE_INDICES);
function clamp01(x) {
    return x < 0 ? 0 : x > 1 ? 1 : x;
}
/**
 * Zero out the pitch dimensions of a dense feature vector. By default a fresh
 * copy is returned; pass `inPlace = true` to mutate and return the same array
 * (useful on the classification hot path to avoid an allocation).
 */
export function maskPitch(features, inPlace = false) {
    const out = inPlace ? features : features.slice();
    for (const i of PITCH_INDEX_SET)
        out[i] = 0;
    return out;
}
const PITCH_NORM_MIN_HZ = 70;
const PITCH_NORM_MAX_HZ = 1000;
const PITCH_LOG_MIN = Math.log2(PITCH_NORM_MIN_HZ);
const PITCH_LOG_SPAN = Math.log2(PITCH_NORM_MAX_HZ) - PITCH_LOG_MIN;
/** Map a fundamental in Hz onto a 0..1 log-frequency scale (0 = unvoiced). */
export function normalizePitchHz(hz) {
    if (!(hz > 0) || !Number.isFinite(hz))
        return 0;
    const v = (Math.log2(hz) - PITCH_LOG_MIN) / (PITCH_LOG_SPAN || 1);
    return clamp01(v);
}
/** Inverse of {@link normalizePitchHz}; returns 0 for the unvoiced sentinel. */
export function denormalizePitchHz(v) {
    if (!(v > 0))
        return 0;
    return 2 ** (PITCH_LOG_MIN + clamp01(v) * (PITCH_LOG_SPAN || 1));
}
export function featureVectorToArray(f) {
    const out = new Array(FEATURE_DIM);
    for (let i = 0; i < FEATURE_DIM; i++) {
        const name = FEATURE_NAMES[i];
        let v = f[name];
        if (name === "pitchHz")
            v = normalizePitchHz(v);
        else if (name === "pitchClass") {
            const pc = v;
            v = pc < 0 ? 0 : pc / 12;
        }
        out[i] = Number.isFinite(v) ? v : 0;
    }
    return out;
}
/**
 * Best-effort inverse of {@link featureVectorToArray}, primarily for
 * diagnostics/round-tripping. Note the transform is lossy: the unvoiced pitch
 * sentinel (pitchClass = -1) collapses to 0 on the way out and cannot be
 * recovered, so it is surfaced here as pitchClass 0.
 */
export function arrayToFeatureVector(arr) {
    const f = {};
    for (let i = 0; i < FEATURE_DIM; i++) {
        const name = FEATURE_NAMES[i];
        let v = arr[i] ?? 0;
        if (name === "pitchHz")
            v = denormalizePitchHz(v);
        else if (name === "pitchClass")
            v = v * 12;
        f[name] = v;
    }
    return f;
}
